<template>
  <component
    :is="to ? 'router-link' : 'div'"
    :to="to"
    class="link-card"
    :data-theme="theme"
    :class="{ 'link-card--list': isList, 'link-card--default': !isList, 'link-card--has-image': image }"
  >
    <div class="link-card__image">
      <ImageComponent v-if="image" v-bind="imageComp" />
      <FontAwesomeIcon v-else :icon="icon" :size="isList ? 'lg' : '3x'" />
    </div>
    <div class="link-card__content">
      <div class="link-card__title">
        <slot name="title">{{ title }}</slot>
      </div>
      <div class="link-card__subtitle">
        <slot name="subtitle">{{ subtitle }}</slot>
      </div>
      <div v-if="!isList" class="link-card__other"><slot /></div>
      <div class="link-card__badges">
        <Badge v-for="(badge, index) in badges" :key="index" v-bind="badge" />
      </div>
    </div>
  </component>
</template>

<script>
import ImageComponent from '@/components/Image'
import Badge from '@/components/Badge'

export default {
  props: {
    image: [String, Object],
    title: String,
    icon: [String, Array],
    subtitle: String,
    badges: Array,
    isList: Boolean,
    to: [String, Object],
    theme: {
      type: String,
      default: 'dark',
    },
    preview: String,
  },
  computed: {
    imageComp() {
      if (typeof this.image === 'string') return { src: this.image, preview: this.preview }
      return this.image
    },
  },
  components: {
    ImageComponent,
    Badge,
  },
}
</script>

<style lang="scss">
.link-card {
  --color: var(--dark);

  position: relative;
  color: var(--color);

  text-decoration: none;

  &:is(:hover, :focus-visible) {
    cursor: pointer;
    .link-card__title h3 {
      text-decoration: underline;
    }
  }

  &--list {
    display: flex;
    gap: 1rem;

    .link-card__image {
      height: 100%;
      border-radius: var(--border-radius-sm);
      background-color: rgba(black, 0.03);

      & > svg {
        opacity: 0.3;
      }
    }

    .link-card__badges {
      margin-top: 0.5rem;
    }
  }

  &--default {
    border-radius: var(--border-radius-md);
    box-shadow: var(--box-shadow);

    background-color: white;

    .link-card__image {
      width: 100%;
      height: 100%;
    }

    .link-card__content {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      padding: 1.5rem;

      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: auto 1fr;
    }

    .link-card__title,
    .link-card__subtitle {
      grid-column-end: span 2;
    }

    .link-card__badges {
      justify-content: flex-end;
      align-self: flex-end;
    }

    &:has(.image--loading) {
      --color: var(--dark) !important;
      .link-card__content {
        background-image: unset !important;
      }
    }

    &[data-theme='light'].link-card--has-image {
      .link-card__content {
        background: linear-gradient(
          170deg,
          rgba(white, 0.8) 15%,
          rgba(255, 255, 255, 0) 55%,
          rgba(255, 255, 255, 0) 100%
        );
      }
    }

    &[data-theme='dark'].link-card--has-image {
      --color: white;

      .link-card__content {
        background: linear-gradient(
          170deg,
          rgba(black, 0.8) 15%,
          rgba(255, 255, 255, 0) 55%,
          rgba(255, 255, 255, 0) 100%
        );
      }
    }
  }

  &__image,
  &__content {
    border-radius: inherit;
    flex-shrink: 0;
  }

  &__badges {
    display: flex;
    gap: 0.25rem;
  }

  &__image {
    aspect-ratio: 5 / 4;
    display: flex;
    justify-content: center;
    align-items: center;

    .image {
      height: 100%;
      width: 100%;
      border-radius: inherit;
    }

    & > svg {
      opacity: 0.2;
    }
  }
}
</style>
